<script setup lang="ts">
import type { GalleryMeta } from '../../../types/gallery-meta'
import type { TopicPageData } from '../../../types/topic-meta'
import { imageUrl } from '../../utils/url.ts'

const route = useRoute()
const router = useRouter()
const toast = useToast()
const { loggedIn } = useUserSession()

const pathSegments = computed(() => {
  const value = route.params.path
  return (Array.isArray(value) ? value : [value])
    .flatMap(segment => typeof segment === 'string' ? segment.split('/') : [])
    .map(segment => segment.trim())
    .filter(Boolean)
})

const galleriesIndex = computed(() => pathSegments.value.indexOf('galleries'))
const galleryName = computed(() => {
  const index = galleriesIndex.value
  return index >= 0 ? (pathSegments.value[index + 1] || '') : ''
})
const topicPath = computed(() => {
  const index = galleriesIndex.value
  return index >= 0 ? pathSegments.value.slice(0, index) : pathSegments.value
})

const topicApiPath = computed(() => `/api/topics/${topicPath.value.join('/')}`)
const galleryBasePath = computed(() => `/topics/${topicPath.value.join('/')}/galleries/${galleryName.value}`)
const breadcrumb = computed(() => {
  const topicPathTopics = topicData.value?.pathTopics || []
  const topicSegments = topicPathTopics.map(item => item.name)

  return [
    ...topicPathTopics.map((item, index) => ({
      label: item.title,
      to: `/topics/${topicSegments.slice(0, index + 1).join('/')}`
    })),
    {
      label: gallery.value?.title || galleryName.value
    }
  ]
})

const [{ data: topicData, error: topicError, refresh: refreshTopicData }, { data: gallery, error: galleryError, refresh: refreshGallery }] = await Promise.all([
  useFetch<TopicPageData>(() => topicApiPath.value),
  useFetch<GalleryMeta>(() => `/api/galleries/${galleryName.value}`)
])

const isEditGalleryOpen = ref(false)
const savingGallery = ref(false)
const isUnlockModalOpen = ref(false)
const unlockingGallery = ref(false)

const editGalleryTitle = ref('')
const editGalleryName = ref('')
const editGalleryDescription = ref('')
const editGalleryHidden = ref(false)
const editGalleryPassword = ref('')
const editGalleryClearPassword = ref(false)
const editGalleryPreviewFile = ref<File | null>(null)
const editGalleryPreviewObjectUrl = ref('')
const editGalleryPreviewInputRef = ref<HTMLInputElement | null>(null)
const editGalleryPreviewDropZoneRef = ref<HTMLElement | null>(null)
const editGalleryPreviewPath = ref<string | null>(null)
const editGalleryRemoveExistingPreview = ref(false)
const unlockPassword = ref('')

watch(editGalleryTitle, (value) => {
  editGalleryName.value = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
})

watch(editGalleryPreviewFile, (file) => {
  if (editGalleryPreviewObjectUrl.value) {
    URL.revokeObjectURL(editGalleryPreviewObjectUrl.value)
    editGalleryPreviewObjectUrl.value = ''
  }

  if (file) {
    editGalleryPreviewObjectUrl.value = URL.createObjectURL(file)
  }
})

onBeforeUnmount(() => {
  if (editGalleryPreviewObjectUrl.value) {
    URL.revokeObjectURL(editGalleryPreviewObjectUrl.value)
  }
})

function setEditGalleryPreviewFile(file?: File | null) {
  if (!file || !file.type.startsWith('image/')) {
    return
  }

  editGalleryPreviewFile.value = file
}

const { isOverDropZone: isEditGalleryPreviewDropZoneOver } = useDropZone(editGalleryPreviewDropZoneRef, (files) => {
  setEditGalleryPreviewFile(files?.[0] || null)
})

function clearEditGalleryPreviewSelection() {
  editGalleryPreviewFile.value = null
  if (editGalleryPreviewInputRef.value) {
    editGalleryPreviewInputRef.value.value = ''
  }
}

function openEditGalleryPreviewPicker() {
  editGalleryPreviewInputRef.value?.click()
}

function onEditGalleryPreviewChange(event: Event) {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  setEditGalleryPreviewFile(file)
}

function markEditGalleryPreviewForRemoval() {
  editGalleryRemoveExistingPreview.value = true
  editGalleryPreviewPath.value = null
}

async function uploadGalleryPreview(galleryId: string, file: File) {
  const formData = new FormData()
  formData.append('galleryId', galleryId)
  formData.append('file', file)

  await $fetch<{ gallery: GalleryMeta }>('/api/galleries/preview', {
    method: 'POST',
    body: formData
  })
}

async function removeGalleryPreview(galleryId: string) {
  await $fetch<{ gallery: GalleryMeta }>('/api/galleries/preview', {
    method: 'DELETE',
    body: { galleryId }
  })
}

function openEditGalleryModal() {
  if (!gallery.value) {
    return
  }

  editGalleryTitle.value = gallery.value.title || ''
  editGalleryName.value = gallery.value.name || ''
  editGalleryDescription.value = gallery.value.description || ''
  editGalleryHidden.value = !!gallery.value.hidden
  editGalleryPassword.value = ''
  editGalleryClearPassword.value = false
  editGalleryPreviewPath.value = gallery.value.uploadedPreviewImagePath || null
  editGalleryRemoveExistingPreview.value = false
  clearEditGalleryPreviewSelection()
  isEditGalleryOpen.value = true
}

async function updateGallery() {
  if (!gallery.value?.id || !editGalleryTitle.value.trim() || savingGallery.value) {
    return
  }

  savingGallery.value = true

  await $fetch<GalleryMeta>(`/api/galleries/${gallery.value.id}`, {
    method: 'PATCH',
    body: {
      title: editGalleryTitle.value,
      name: editGalleryName.value,
      description: editGalleryDescription.value,
      hidden: editGalleryHidden.value,
      password: editGalleryPassword.value,
      clearPassword: editGalleryClearPassword.value
    }
  }).then(async (updated) => {
    if (!gallery.value) {
      return
    }

    if (editGalleryRemoveExistingPreview.value && gallery.value?.uploadedPreviewImagePath) {
      await removeGalleryPreview(gallery.value.id).catch((err) => {
        toast.add({
          color: 'amber',
          title: 'Gallery updated, but preview removal failed',
          description: err.data?.message || err.message
        })
      })
    }

    if (editGalleryPreviewFile.value) {
      await uploadGalleryPreview(gallery.value.id, editGalleryPreviewFile.value).catch((err) => {
        toast.add({
          color: 'amber',
          title: 'Gallery updated without new preview image',
          description: err.data?.message || err.message
        })
      })
    }

    await refreshGallery()
    isEditGalleryOpen.value = false

    const nextPath = `/topics/${topicPath.value.join('/')}/galleries/${updated.name}`

    if (nextPath !== route.path) {
      await router.push(nextPath)
    }
  }).catch((err) => {
    toast.add({
      color: 'red',
      title: 'Failed to update gallery',
      description: err.data?.message || err.message
    })
  }).finally(() => {
    savingGallery.value = false
  })
}

async function unlockGallery() {
  if (!unlockPassword.value.trim() || unlockingGallery.value) {
    return
  }

  unlockingGallery.value = true

  await $fetch('/api/galleries/unlock', {
    method: 'POST',
    body: {
      name: galleryName.value,
      password: unlockPassword.value
    }
  }).then(async () => {
    await Promise.all([
      refreshTopicData(),
      refreshGallery()
    ])

    isUnlockModalOpen.value = false
    unlockPassword.value = ''
  }).catch((err) => {
    toast.add({
      color: 'red',
      title: 'Invalid gallery password',
      description: err.data?.message || err.message
    })
  }).finally(() => {
    unlockingGallery.value = false
  })
}

if (galleryError.value?.statusCode === 401) {
  isUnlockModalOpen.value = true
}

if (topicError.value || galleryError.value || !topicData.value || !gallery.value || gallery.value.topicId !== topicData.value.topic.id) {
  if (galleryError.value?.statusCode === 401) {
    // keep page rendered so unlock modal can be shown
  }
  else {
    throw createError({
      statusCode: 404,
      statusMessage: `This gallery (${galleryName.value}) does not exist under the requested topic.`
    })
  }
}
</script>

<template>
  <main class="min-h-screen flex flex-col">
    <UModal
      v-model:open="isUnlockModalOpen"
      title="Gallery password required"
      :dismissible="false"
      :close="false"
    >
      <template #body>
        <form
          class="flex flex-col gap-4"
          @submit.prevent="unlockGallery"
        >
          <UInput
            v-model="unlockPassword"
            type="password"
            placeholder="Enter gallery password"
            autocomplete="current-password"
            required
          />
          <div class="flex justify-end">
            <UButton
              type="submit"
              :loading="unlockingGallery"
              label="Unlock"
              color="primary"
            />
          </div>
        </form>
      </template>
    </UModal>

    <div class="flex-1">
      <ImageGallery
        v-if="gallery"
        :gallery-id="gallery?.id"
        :gallery-name="galleryName"
        :gallery-title="gallery?.title"
        :gallery-description="gallery?.description"
        :preview-image-path="gallery?.uploadedPreviewImagePath || gallery?.previewImagePath || ''"
        :youtube-embed-urls="gallery?.youtubeEmbedUrls || []"
        :gallery-entry-order="gallery?.galleryEntryOrder || []"
        :breadcrumb="breadcrumb"
        :gallery-base-path="galleryBasePath"
      >
        <template #header-actions="{ reverseOrder, sortByName, reversingOrder }">
          <div class="flex items-center gap-2">
            <UButton
              v-if="loggedIn"
              icon="i-heroicons-arrows-up-down-20-solid"
              color="neutral"
              variant="subtle"
              label="Reverse"
              size="xs"
              aria-label="Reverse gallery order"
              :loading="reversingOrder"
              @click="reverseOrder"
            />
            <UButton
              v-if="loggedIn"
              icon="i-heroicons-bars-arrow-down-20-solid"
              color="neutral"
              variant="subtle"
              label="Sort by name"
              size="xs"
              aria-label="Sort gallery by name"
              :loading="reversingOrder"
              @click="sortByName"
            />
            <UModal
              v-if="loggedIn"
              v-model:open="isEditGalleryOpen"
              title="Edit Gallery"
              :close="{ color: 'gray', variant: 'outline', class: 'rounded-full' }"
            >
              <UButton
                icon="i-heroicons-pencil-square-20-solid"
                color="primary"
                variant="subtle"
                label="Edit"
                size="xs"
                aria-label="Edit gallery"
                @click="openEditGalleryModal"
              />
              <template #body>
                <form
                  class="flex flex-col gap-4"
                  @submit.prevent="updateGallery"
                >
                  <UInput
                    v-model="editGalleryTitle"
                    placeholder="Title"
                    required
                  />
                  <UInput
                    v-model="editGalleryName"
                    placeholder="Slug"
                  />
                  <input
                    ref="editGalleryPreviewInputRef"
                    type="file"
                    accept="image/*"
                    class="hidden"
                    @change="onEditGalleryPreviewChange"
                  >
                  <UTextarea
                    v-model="editGalleryDescription"
                    placeholder="Short description"
                    :rows="3"
                  />
                  <div class="space-y-2">
                    <p class="text-sm text-gray-300">
                      Gallery preview image
                    </p>
                    <div
                      v-if="editGalleryPreviewPath && !editGalleryPreviewObjectUrl"
                      class="flex items-center gap-3 rounded-lg border border-white/10 bg-black/30 p-3"
                    >
                      <img
                        :src="imageUrl(editGalleryPreviewPath, 'thumb')"
                        alt="Current gallery preview"
                        class="h-14 w-14 rounded-md object-cover border border-white/10"
                      >
                      <UButton
                        type="button"
                        size="xs"
                        color="red"
                        variant="soft"
                        label="Remove"
                        class="ml-auto"
                        @click="markEditGalleryPreviewForRemoval"
                      />
                    </div>
                    <div
                      v-else-if="editGalleryPreviewObjectUrl"
                      class="flex items-center gap-3 rounded-lg border border-white/10 bg-black/30 p-3"
                    >
                      <img
                        :src="editGalleryPreviewObjectUrl"
                        alt="Selected gallery preview"
                        class="h-14 w-14 rounded-md object-cover border border-white/10"
                      >
                      <UButton
                        type="button"
                        size="xs"
                        color="red"
                        variant="soft"
                        label="Remove"
                        class="ml-auto"
                        @click="clearEditGalleryPreviewSelection"
                      />
                    </div>
                    <button
                      v-else
                      ref="editGalleryPreviewDropZoneRef"
                      type="button"
                      class="w-full rounded-lg border border-dashed p-4 text-sm transition-colors text-left"
                      :class="isEditGalleryPreviewDropZoneOver ? 'border-primary-400 bg-primary-500/10 text-white' : 'border-white/20 bg-black/20 text-gray-300 hover:border-white/40'"
                      @click="openEditGalleryPreviewPicker"
                    >
                      Click or drag & drop an image
                    </button>
                  </div>
                  <UCheckbox
                    v-model="editGalleryHidden"
                    label="Hidden (not shown in topic gallery listing)"
                  />
                  <UInput
                    v-model="editGalleryPassword"
                    type="password"
                    autocomplete="new-password"
                    placeholder="Set new password (optional)"
                  />
                  <UCheckbox
                    v-model="editGalleryClearPassword"
                    label="Clear existing password"
                  />
                  <div class="flex justify-end">
                    <UButton
                      type="submit"
                      :loading="savingGallery"
                      label="Save"
                      color="primary"
                    />
                  </div>
                </form>
              </template>
            </UModal>
          </div>
        </template>
      </ImageGallery>
    </div>

    <AppCopyrightFooter v-if="gallery" />
  </main>
</template>
